import React, { useState, useMemo } from 'react';
import GIQuestionHeader from './GIQuestionHeader';
import GINumericQuestion from './GINumericQuestion';
import StatTile from '../ui/StatTile';

const GIDistributionQuestion = ({ questionId, questionText, questionOptions, questionConstraints, answers, isCollapsed = false, onToggleCollapse }) => {
  const [activeTab, setActiveTab] = useState('summary');

  // Préparer les données pour chaque sous-question
  const distributionData = useMemo(() => {
    if (!answers || !answers.length || !questionOptions) {
      return {
        subQuestions: [],
        totalResponses: 0,
        sumStats: { average: 0, target: 0, compliance: 0 }
      };
    }

    const subQuestions = [];
    let totalResponses = 0;
    const sumValues = [];

    // Pour chaque option, créer une sous-question
    questionOptions.forEach(option => {
      const subQuestionAnswers = [];

      // Transformer les réponses pour cette sous-question
      answers.forEach(answer => {
        if (answer.Answers[questionId] && typeof answer.Answers[questionId] === 'object') {
          const distributionAnswers = answer.Answers[questionId];
          const subQuestionValue = distributionAnswers[option.id.toString()];

          if (subQuestionValue !== undefined) {
            // Obtenir le nom du promoteur selon le format disponible
            let promoterName;
            if (answer.PromoterName) {
              promoterName = answer.PromoterName;
            } else {
              promoterName = 'Promoteur inconnu';
            }

            subQuestionAnswers.push({
              SurveySentId: answer.SurveySentId,
              PromoterName: promoterName,
              AnswerDate: answer.AnswerDate,
              Answers: {
                [option.id]: subQuestionValue
              }
            });
          }
        }
      });

      subQuestions.push({
        id: option.id,
        text: option.text,
        answers: subQuestionAnswers,
        questionId: option.id.toString()
      });

      if (subQuestionAnswers.length > 0) {
        totalResponses = Math.max(totalResponses, subQuestionAnswers.length);
      }
    });

    // Calculer les statistiques de somme si on a une contrainte
    if (questionConstraints?.sum || questionConstraints?.max) {
      answers.forEach(answer => {
        if (answer.Answers[questionId] && typeof answer.Answers[questionId] === 'object') {
          const distributionAnswers = answer.Answers[questionId];
          let sum = 0;

          Object.values(distributionAnswers).forEach(value => {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              sum += numValue;
            }
          });

          sumValues.push(sum);
        }
      });
    }

    const targetSum = parseFloat(questionConstraints?.sum || questionConstraints?.max || 0);
    const averageSum = sumValues.length > 0 ? sumValues.reduce((a, b) => a + b, 0) / sumValues.length : 0;
    const compliance = sumValues.length > 0 ? (sumValues.filter(sum => sum === targetSum).length / sumValues.length) * 100 : 0;

    return {
      subQuestions,
      totalResponses,
      sumStats: {
        average: parseFloat(averageSum.toFixed(2)),
        target: targetSum,
        compliance: parseFloat(compliance.toFixed(1)),
        values: sumValues
      }
    };
  }, [questionId, questionOptions, questionConstraints, answers]);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <GIQuestionHeader
        questionId={questionId}
        questionText={questionText}
        questionType="Distribution"
        badgeLabel="Distrib"
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
      />

      {!isCollapsed && (
        <>
      {/* Tabs */}
      <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <nav className="flex">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'summary'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Résumé
          </button>
          <button
            onClick={() => setActiveTab('detail')}
            className={`px-4 py-2 text-sm font-medium ${
              activeTab === 'detail'
                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Détail par option
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'summary' ? (
          <div className="space-y-4">
            {/* Statistiques de contrainte de somme */}
            {distributionData.sumStats.target > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <StatTile
                  value={distributionData.sumStats.average.toLocaleString('fr-FR')}
                  label="Somme moyenne"
                  color="blue"
                  size="s"
                  subValue={`Target: ${distributionData.sumStats.target}`}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  }
                />
                <StatTile
                  value={`${distributionData.sumStats.compliance}%`}
                  label="Conformité"
                  color={distributionData.sumStats.compliance >= 80 ? "green" : distributionData.sumStats.compliance >= 60 ? "yellow" : "red"}
                  size="s"
                  subValue={`${distributionData.totalResponses.toLocaleString('fr-FR')} réponses`}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
                <StatTile
                  value={distributionData.subQuestions.length.toLocaleString('fr-FR')}
                  label="Options"
                  color="purple"
                  size="s"
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  }
                />
              </div>
            )}

            {/* Répartition moyenne par option */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Répartition moyenne par option
              </h4>
              <div className="space-y-3">
                {distributionData.subQuestions.map((subQuestion, index) => {
                  // Calculer la moyenne pour cette option
                  const values = subQuestion.answers.map(answer =>
                    parseFloat(answer.Answers[subQuestion.id]) || 0
                  );
                  const average = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
                  const maxAverage = Math.max(...distributionData.subQuestions.map(sq => {
                    const vals = sq.answers.map(a => parseFloat(a.Answers[sq.id]) || 0);
                    return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
                  }));

                  const percentage = maxAverage > 0 ? (average / maxAverage) * 100 : 0;

                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-purple-500'];
                  const color = colors[index % colors.length];

                  return (
                    <div key={subQuestion.id} className="space-y-1">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-700 dark:text-gray-300 font-medium truncate max-w-xs">
                          {subQuestion.text}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                          {average.toFixed(1)} en moyenne
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 relative overflow-hidden">
                        <div
                          className={`h-4 rounded-full ${color} transition-all duration-300`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                        <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                          {average.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {distributionData.totalResponses === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucune réponse pour cette question de distribution
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Afficher chaque sous-question avec GINumericQuestion */}
            {distributionData.subQuestions.map((subQuestion) => (
              <div key={subQuestion.id} className="border border-gray-100 dark:border-gray-600 rounded-lg overflow-hidden">
                <div className="bg-gray-25 dark:bg-gray-750 p-2 border-b border-gray-100 dark:border-gray-600">
                  <h5 className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Option {subQuestion.id}: {subQuestion.text}
                  </h5>
                </div>
                <div className="p-2">
                  <GINumericQuestion
                    questionId={subQuestion.questionId}
                    questionText={subQuestion.text}
                    answers={subQuestion.answers}
                    promoters={[]} // Pas nécessaire ici
                  />
                </div>
              </div>
            ))}

            {distributionData.subQuestions.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Aucune option trouvée pour cette question de distribution
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default GIDistributionQuestion;